/**
 * SSR-Safe Utilities for CodeRide
 * Comprehensive browser API wrappers that work in both SSR and client environments
 */

import React from 'react';

// Check if we're in a browser environment
export const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';

// Check if we're in SSR environment
export const isSSR = !isBrowser;

/**
 * SSR-safe document operations
 */
export const ssrDocument = {
  getElementById: (id: string): Element | null => {
    if (!isBrowser) return null;
    return document.getElementById(id);
  },
  
  querySelector: (selector: string): Element | null => {
    if (!isBrowser) return null;
    return document.querySelector(selector);
  },
  
  querySelectorAll: (selector: string): NodeListOf<Element> | [] => {
    if (!isBrowser) return [] as any;
    return document.querySelectorAll(selector);
  },
  
  createElement: (tagName: string): HTMLElement | null => {
    if (!isBrowser) return null;
    return document.createElement(tagName);
  },
  
  addEventListener: (event: string, handler: EventListener): void => {
    if (!isBrowser) return;
    document.addEventListener(event, handler);
  },
  
  removeEventListener: (event: string, handler: EventListener): void => {
    if (!isBrowser) return;
    document.removeEventListener(event, handler);
  },
  
  get cookie(): string {
    if (!isBrowser) return '';
    return document.cookie;
  },
  
  set cookie(value: string) {
    if (!isBrowser) return;
    document.cookie = value;
  },
  
  get head(): HTMLHeadElement | null {
    if (!isBrowser) return null;
    return document.head;
  },
  
  get body(): HTMLElement | null {
    if (!isBrowser) return null;
    return document.body;
  },
  
  get documentElement(): HTMLElement | null {
    if (!isBrowser) return null;
    return document.documentElement;
  }
};

/**
 * SSR-safe window operations
 */
export const ssrWindow = {
  addEventListener: (event: string, handler: EventListener): void => {
    if (!isBrowser) return;
    window.addEventListener(event, handler);
  },
  
  removeEventListener: (event: string, handler: EventListener): void => {
    if (!isBrowser) return;
    window.removeEventListener(event, handler);
  },
  
  scrollTo: (options: ScrollToOptions | number, y?: number): void => {
    if (!isBrowser) return;
    if (typeof options === 'number') {
      window.scrollTo(options, y || 0);
    } else {
      window.scrollTo(options);
    }
  },
  
  open: (url: string, target?: string, features?: string): Window | null => {
    if (!isBrowser) return null;
    return window.open(url, target, features);
  },
  
  matchMedia: (query: string) => {
    if (!isBrowser) {
      return {
        matches: false,
        addEventListener: () => {},
        removeEventListener: () => {}
      };
    }
    return window.matchMedia(query);
  },
  
  get location() {
    if (!isBrowser) {
      return {
        href: '',
        pathname: '/',
        search: '',
        hash: ''
      };
    }
    return window.location;
  },
  
  get innerWidth(): number {
    if (!isBrowser) return 1024; // Default width for SSR
    return window.innerWidth;
  },
  
  get innerHeight(): number {
    if (!isBrowser) return 768; // Default height for SSR
    return window.innerHeight;
  },
  
  get scrollY(): number {
    if (!isBrowser) return 0;
    return window.scrollY;
  },
  
  dispatchEvent: (event: Event): boolean => {
    if (!isBrowser) return false;
    return window.dispatchEvent(event);
  }
};

/**
 * SSR-safe localStorage operations
 */
export const ssrLocalStorage = {
  getItem: (key: string): string | null => {
    if (!isBrowser) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    if (!isBrowser) return;
    try {
      localStorage.setItem(key, value);
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  },
  
  removeItem: (key: string): void => {
    if (!isBrowser) return;
    try {
      localStorage.removeItem(key);
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  },
  
  clear: (): void => {
    if (!isBrowser) return;
    try {
      localStorage.clear();
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  }
};

/**
 * SSR-safe sessionStorage operations
 */
export const ssrSessionStorage = {
  getItem: (key: string): string | null => {
    if (!isBrowser) return null;
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  
  setItem: (key: string, value: string): void => {
    if (!isBrowser) return;
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  },
  
  removeItem: (key: string): void => {
    if (!isBrowser) return;
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  },
  
  clear: (): void => {
    if (!isBrowser) return;
    try {
      sessionStorage.clear();
    } catch {
      // Silently fail in SSR or when storage is unavailable
    }
  }
};

/**
 * SSR-safe custom event creation
 */
export const createSSRSafeEvent = (eventName: string, detail?: any): CustomEvent | null => {
  if (!isBrowser) return null;
  return new CustomEvent(eventName, { detail });
};

/**
 * SSR-safe intersection observer
 */
export const createSSRSafeIntersectionObserver = (
  callback: IntersectionObserverCallback,
  options?: IntersectionObserverInit
): IntersectionObserver | null => {
  if (!isBrowser || !window.IntersectionObserver) return null;
  return new IntersectionObserver(callback, options);
};

/**
 * SSR-safe mutation observer
 */
export const createSSRSafeMutationObserver = (
  callback: MutationCallback
): MutationObserver | null => {
  if (!isBrowser || !window.MutationObserver) return null;
  return new MutationObserver(callback);
};

/**
 * SSR-safe timeout functions
 */
export const ssrSetTimeout = (callback: () => void, delay: number): number | null => {
  if (!isBrowser) return null;
  return window.setTimeout(callback, delay);
};

export const ssrClearTimeout = (id: number | null): void => {
  if (!isBrowser || id === null) return;
  window.clearTimeout(id);
};

/**
 * SSR-safe interval functions
 */
export const ssrSetInterval = (callback: () => void, delay: number): number | null => {
  if (!isBrowser) return null;
  return window.setInterval(callback, delay);
};

export const ssrClearInterval = (id: number | null): void => {
  if (!isBrowser || id === null) return;
  window.clearInterval(id);
};

/**
 * SSR-safe element operations
 */
export const ssrElementOperations = {
  scrollIntoView: (element: Element | null, options?: ScrollIntoViewOptions): void => {
    if (!isBrowser || !element) return;
    element.scrollIntoView(options);
  },
  
  getBoundingClientRect: (element: Element | null): DOMRect => {
    if (!isBrowser || !element) {
      return {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: 0,
        height: 0,
        x: 0,
        y: 0,
        toJSON: () => ({})
      };
    }
    return element.getBoundingClientRect();
  },
  
  appendChild: (parent: Element | null, child: Element | null): void => {
    if (!isBrowser || !parent || !child) return;
    parent.appendChild(child);
  },
  
  removeChild: (parent: Element | null, child: Element | null): void => {
    if (!isBrowser || !parent || !child) return;
    try {
      parent.removeChild(child);
    } catch {
      // Silently fail if child is not found
    }
  },
  
  contains: (parent: Element | null, child: Element | null): boolean => {
    if (!isBrowser || !parent || !child) return false;
    return parent.contains(child);
  }
};

/**
 * Hook for SSR-safe useEffect that only runs on client
 */
export const useSSRSafeEffect = (effect: () => void | (() => void), deps?: React.DependencyList) => {
  React.useEffect(() => {
    if (!isBrowser) return;
    return effect();
  }, deps);
};

/**
 * Hook for SSR-safe layout effect
 */
export const useSSRSafeLayoutEffect = (effect: () => void | (() => void), deps?: React.DependencyList) => {
  // Use useEffect on server, useLayoutEffect on client
  const useIsomorphicLayoutEffect = isBrowser ? React.useLayoutEffect : React.useEffect;
  
  useIsomorphicLayoutEffect(() => {
    if (!isBrowser) return;
    return effect();
  }, deps);
};

/**
 * SSR-safe state initialization
 */
export const useSSRSafeState = <T>(clientInitialValue: T, serverInitialValue?: T) => {
  const [state, setState] = React.useState<T>(
    isBrowser ? clientInitialValue : (serverInitialValue ?? clientInitialValue)
  );
  
  // Sync state on hydration
  React.useEffect(() => {
    if (serverInitialValue !== undefined && state !== clientInitialValue) {
      setState(clientInitialValue);
    }
  }, []);
  
  return [state, setState] as const;
};

/**
 * Utility to safely execute browser-only code
 */
export const runOnClient = (callback: () => void): void => {
  if (isBrowser) {
    callback();
  }
};

/**
 * Utility to safely execute server-only code
 */
export const runOnServer = (callback: () => void): void => {
  if (isSSR) {
    callback();
  }
};

/**
 * Get safe window dimensions
 */
export const getSSRSafeWindowDimensions = () => {
  return {
    width: ssrWindow.innerWidth,
    height: ssrWindow.innerHeight
  };
};

/**
 * Check if element is in viewport (SSR-safe)
 */
export const isElementInViewport = (element: Element | null): boolean => {
  if (!isBrowser || !element) return false;
  
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= window.innerHeight &&
    rect.right <= window.innerWidth
  );
};

export default {
  isBrowser,
  isSSR,
  ssrDocument,
  ssrWindow,
  ssrLocalStorage,
  ssrSessionStorage,
  createSSRSafeEvent,
  createSSRSafeIntersectionObserver,
  createSSRSafeMutationObserver,
  ssrSetTimeout,
  ssrClearTimeout,
  ssrSetInterval,
  ssrClearInterval,
  ssrElementOperations,
  useSSRSafeEffect,
  useSSRSafeLayoutEffect,
  useSSRSafeState,
  runOnClient,
  runOnServer,
  getSSRSafeWindowDimensions,
  isElementInViewport
};
